import React, { useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import { sendSupportMessage } from '../../services/supportApi';

const SUPPORT_EMAIL = 'support@retechsys.ai';

export function SupportContact({
  email = '',
  triggerLabel = 'Contact support',
  triggerClassName,
  variant = 'button',
}) {
  const triggerClass =
    triggerClassName ||
    (variant === 'link'
      ? 'provider-link'
      : 'provider-btn provider-btn--secondary provider-btn--sm provider-btn--inline');
  const { user } = useAuth();
  const accountEmail = email || user?.attributes?.email || '';

  const [open, setOpen] = useState(false);
  const [fromEmail, setFromEmail] = useState('');
  const [topic, setTopic] = useState('General question');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errorText, setErrorText] = useState('');

  const openDialog = () => {
    setStatus('idle');
    setErrorText('');
    setFromEmail((current) => current || accountEmail);
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    if (status === 'sent') setMessage('');
    setStatus('idle');
    setErrorText('');
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorText('');
    try {
      const accessToken = localStorage.getItem('accessToken');
      await sendSupportMessage({
        accessToken: accessToken || undefined,
        email: fromEmail.trim(),
        topic,
        message,
      });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setErrorText(err.message || 'We could not send your message.');
    }
  };

  return (
    <>
      <button type="button" className={triggerClass} onClick={openDialog}>
        {triggerLabel}
      </button>

      {open && (
        <div className="provider-modal-backdrop" onClick={closeDialog} role="presentation">
          <div
            className="provider-modal"
            role="dialog"
            aria-labelledby="support-dialog-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="provider-modal-header">
              <div>
                <p className="provider-eyebrow" style={{ marginBottom: 6 }}>
                  We&apos;re here to help
                </p>
                <h2 id="support-dialog-title" className="provider-modal-title">
                  Email support
                </h2>
              </div>
              <button
                type="button"
                className="provider-modal-close"
                onClick={closeDialog}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {status === 'sent' ? (
              <div className="provider-support-sent">
                <p className="provider-modal-lead">
                  <strong>Message sent.</strong> It&apos;s on its way to our support team — we
                  typically respond to <strong>{fromEmail || 'your email'}</strong> within one
                  business day.
                </p>
                <div className="provider-form-actions" style={{ marginTop: 20 }}>
                  <button
                    type="button"
                    className="provider-btn provider-btn--primary provider-btn--inline"
                    onClick={closeDialog}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="provider-modal-lead">
                  Send us a message right from here — we typically respond within one business day.
                </p>

                <form onSubmit={handleSend} className="provider-modal-form">
                  <div className="provider-form-group">
                    <label htmlFor="support-email" className="provider-label">
                      Your email <span className="provider-label-hint">— where we reply</span>
                    </label>
                    <input
                      id="support-email"
                      type="email"
                      className="provider-input"
                      required
                      placeholder="you@yourstore.com"
                      value={fromEmail}
                      onChange={(e) => setFromEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </div>

                  <div className="provider-form-group">
                    <label htmlFor="support-topic" className="provider-label">
                      Topic
                    </label>
                    <select
                      id="support-topic"
                      className="provider-select"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                    >
                      <option>General question</option>
                      <option>Device setup</option>
                      <option>Billing &amp; invoices</option>
                      <option>Transactions &amp; usage</option>
                      <option>Account access</option>
                    </select>
                  </div>

                  <div className="provider-form-group">
                    <label htmlFor="support-message" className="provider-label">
                      Message
                    </label>
                    <textarea
                      id="support-message"
                      className="provider-textarea"
                      rows={5}
                      required
                      maxLength={5000}
                      placeholder="Describe what you need help with…"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </div>

                  {status === 'error' && (
                    <div className="provider-alert provider-alert--error" role="alert">
                      {errorText}{' '}
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="provider-link">
                        Email us directly instead
                      </a>
                      .
                    </div>
                  )}

                  <div className="provider-form-actions" style={{ marginTop: 20 }}>
                    <button
                      type="button"
                      className="provider-btn provider-btn--secondary"
                      onClick={closeDialog}
                      disabled={status === 'sending'}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="provider-btn provider-btn--primary provider-btn--inline"
                      disabled={status === 'sending'}
                    >
                      {status === 'sending' ? 'Sending…' : 'Send message'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default SupportContact;
